import { Injectable, signal } from '@angular/core';
import { ApiBaseService } from './api-base.service';

export type NotificationCategory =
  | 'Projects'
  | 'Tasks'
  | 'ProjectHub'
  | 'ContractsAndDeadlines'
  | 'Costs'
  | 'GeneralUpdates';

export interface NotificationItem {
  // The recipient row, not the event - this is what mark-read acts on.
  id: number;
  notificationId: number;
  category: NotificationCategory;
  type: string;
  titleKey: string;
  payloadJson?: string;
  deepLink?: string;
  actorEmail?: string;
  actorName?: string;
  projectId?: number;
  siteId?: number;
  entityType?: string;
  entityId?: number;
  occurredAt: string;
  readAt?: string;
  isRead: boolean;
}

export interface NotificationListResult {
  items: NotificationItem[];
  totalCount: number;
  unreadCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface NotificationListQuery {
  pageNumber?: number;
  pageSize?: number;
  unreadOnly?: boolean;
  categories?: NotificationCategory[];
}

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  Projects: $localize`:@@notificationCategoryProjects:Projects`,
  Tasks: $localize`:@@notificationCategoryTasks:Tasks`,
  ProjectHub: $localize`:@@notificationCategoryProjectHub:Project Hub`,
  ContractsAndDeadlines: $localize`:@@notificationCategoryContracts:Contracts & Deadlines`,
  Costs: $localize`:@@notificationCategoryCosts:Costs`,
  GeneralUpdates: $localize`:@@notificationCategoryGeneral:General Updates`,
};

@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {
  // Shared so the bell badge and the panel never disagree about the count.
  readonly unreadCount = signal(0);

  constructor(private apiService: ApiBaseService) {}

  // API soft-error envelope: filter-level rejections come back as HTTP 200 with
  // { hasError: true, feedback: <message> }, not as a thrown HTTP error.
  private async unwrap<T>(request: Promise<any>): Promise<T> {
    const response = await request;
    if (response?.hasError) {
      throw new Error(response?.feedback || $localize`:@@notificationRequestFailed:The request could not be processed.`);
    }
    return response as T;
  }

  async getNotifications(query: NotificationListQuery = {}): Promise<NotificationListResult> {
    const result = await this.unwrap<NotificationListResult>(
      this.apiService.httpRequest(
        'NotificationCenterService/GetNotifications',
        {
          PageNumber: query.pageNumber ?? 1,
          PageSize: query.pageSize ?? 20,
          UnreadOnly: query.unreadOnly ?? false,
          Categories: query.categories ?? [],
        },
        true,
        false,
      ),
    );

    this.unreadCount.set(result.unreadCount ?? 0);
    return result;
  }

  async refreshUnreadCount(): Promise<number> {
    const result = await this.unwrap<{ unreadCount: number }>(
      this.apiService.httpRequest('NotificationCenterService/GetUnreadCount', {}, true, false),
    );

    this.unreadCount.set(result.unreadCount ?? 0);
    return this.unreadCount();
  }

  async markRead(ids: number[]): Promise<number> {
    if (!ids.length) return this.unreadCount();

    const result = await this.unwrap<{ unreadCount: number }>(
      this.apiService.httpRequest('NotificationCenterService/MarkRead', { Ids: ids }, true, false),
    );

    this.unreadCount.set(result.unreadCount ?? 0);
    return this.unreadCount();
  }

  async markAllRead(): Promise<void> {
    await this.unwrap(this.apiService.httpRequest('NotificationCenterService/MarkAllRead', {}, true, false));
    this.unreadCount.set(0);
  }
}
